import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useCart } from 'contexts/CartContext';
import { useProducts } from 'contexts/ProductsContext';
import { useSizePreference } from 'contexts/SizePreference';
import { getSuggestions } from 'lib/suggestions';
import { shareProduct } from 'lib/shareUtils';

const COLOR_MAP = {
  'white': '#FFFFFF', 'black': '#151515', 'off white': '#FEF8E1',
  'navy blue': '#000A1A', 'navy': '#000A1A', 'maroon': '#31050C',
  'lavender': '#E5DBFF', 'baby blue': '#BFE5FF', 'flamingo': '#EDA8B5',
  'jade': '#A4C9A3', 'red': '#8A0106', 'bottle green': '#083717', 'green': '#083717',
  'light baby pink': '#FFD4E9', 'cream': '#f5f0e1', 'beige': '#d4c5a9',
  'blue': '#1565c0', 'light blue': '#64b5f6', 'sage green': '#8fbc8f',
  'olive': '#6b6b3a', 'olive green': '#6b6b3a', 'mint': '#98ff98',
  'grey': '#757575', 'gray': '#757575', 'charcoal': '#36454f',
  'pink': '#e91e63', 'light pink': '#f8bbd0', 'baby pink': '#f4c2c2',
  'hot pink': '#ff69b4', 'dusty pink': '#d4a5a5', 'dusty rose': '#c4767a',
  'rose': '#c4767a', 'lilac': '#c8a2c8', 'purple': '#7b1fa2',
  'yellow': '#f9a825', 'mustard': '#c8a415', 'orange': '#ef6c00',
  'brown': '#5d4037', 'burgundy': '#800020', 'teal': '#00796b',
  'coral': '#ff7f50', 'peach': '#ffcba4', 'sand': '#c2b280',
  'khaki': '#c3b091', 'tan': '#d2b48c',
};

function getColorHex(name) {
  return COLOR_MAP[name.toLowerCase()] || '#888';
}

function MobileZoom({ images, currentImage, setCurrentImage, onClose }) {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const touchState = useRef({ lastDist: 0, lastPos: null, isPinching: false, lastTap: 0 });
  const totalImages = images.length;

  function getTouchDist(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      touchState.current.isPinching = true;
      touchState.current.lastDist = getTouchDist(e.touches);
    } else if (e.touches.length === 1 && scale > 1) {
      touchState.current.lastPos = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  }, [scale]);

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length === 2 && touchState.current.isPinching) {
      e.preventDefault();
      const dist = getTouchDist(e.touches);
      const delta = dist / touchState.current.lastDist;
      touchState.current.lastDist = dist;
      setScale(s => {
        const next = Math.max(0.5, Math.min(2, s * delta));
        // If pinching below 1, close
        if (next < 0.8) { onClose(); return 1; }
        return next;
      });
    } else if (e.touches.length === 1 && scale > 1 && touchState.current.lastPos) {
      const dx = e.touches[0].clientX - touchState.current.lastPos.x;
      const dy = e.touches[0].clientY - touchState.current.lastPos.y;
      touchState.current.lastPos = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      setTranslate(t => ({ x: t.x + dx, y: t.y + dy }));
    }
  }, [scale, onClose]);

  const handleTouchEnd = useCallback((e) => {
    if (e.touches.length < 2) {
      touchState.current.isPinching = false;
    }
    if (e.touches.length === 0) {
      touchState.current.lastPos = null;

      // Double-tap detection
      const now = Date.now();
      if (now - touchState.current.lastTap < 300 && !touchState.current.isPinching) {
        // Double tap: toggle zoom
        if (scale > 1.1) {
          setScale(1);
          setTranslate({ x: 0, y: 0 });
        } else {
          setScale(2);
          setTranslate({ x: 0, y: 0 });
        }
        touchState.current.lastTap = 0;
        return;
      }
      touchState.current.lastTap = now;

      // Snap back if scale is near 1
      if (scale <= 1.05) {
        setScale(1);
        setTranslate({ x: 0, y: 0 });
      }
    }
  }, [scale]);

  // Reset transform when switching images
  useEffect(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, [currentImage]);

  // Browser back to close
  useEffect(() => {
    window.history.pushState({ view: 'zoom' }, '');
    function handlePop() { onClose(); }
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [onClose]);

  return (
    <div
      className="mzoom"
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <button className="mzoom__close" onClick={onClose}>✕</button>
      <div
        className="mzoom__img"
        style={{ transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})` }}
      >
        <img src={images[currentImage]} alt="" />
      </div>
      <button className="mzoom__nav mzoom__nav--left" onClick={() => setCurrentImage(i => i > 0 ? i - 1 : totalImages - 1)}>◀</button>
      <button className="mzoom__nav mzoom__nav--right" onClick={() => setCurrentImage(i => i < totalImages - 1 ? i + 1 : 0)}>▶</button>
      <div className="mzoom__counter">{currentImage + 1} / {totalImages}</div>
    </div>
  );
}

function MobileFabricCare({ description }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="mpv-fabric-care">
      <div className="mpv-fabric-care__label" onClick={() => setOpen(o => !o)}>
        <span className="mpv-fabric-care__arrow">{open ? '▼' : '▶'}</span> Fabric & Care
      </div>
      {open && (
        <div className="mpv-fabric-care__text">
          {description.replace(/&nbsp;/g, ' ').split(/(?=(?:Fabric|Fit|Care|Design):)/g).filter(s => s.trim() && !s.trim().match(/^_+$/)).map((s, i) => <div key={i}>{s.trim()}</div>)}
        </div>
      )}
    </div>
  );
}

function MobileProductView({ product, onBack, onShowDialog, onViewProduct, onOpenCart, onOpenCategory, initialSelections = {} }) {
  const { addItem } = useCart();
  const { products: allProducts } = useProducts();
  const { sizePreference } = useSizePreference();
  const suggested = getSuggestions(product, allProducts, 2);
  const [currentImage, setCurrentImage] = useState(0);
  const autoSize = !initialSelections.size
    ? (product?.sizes?.length === 1 ? product.sizes[0] : (product?.sizes?.includes(sizePreference) ? sizePreference : ''))
    : '';
  const [selectedSize, setSelectedSize] = useState(initialSelections.size || autoSize);
  const [wasAutoSelected, setWasAutoSelected] = useState(!!autoSize && !initialSelections.size && product?.sizes?.length > 1);
  const [selectedColor, setSelectedColor] = useState(initialSelections.color || product?.defaultColor || product?.colors?.[0] || '');
  const [quantity, setQuantity] = useState(initialSelections.quantity || 1);
  const [showZoom, setShowZoom] = useState(false);
  const swipeRef = useRef({ startX: 0, startY: 0 });

  // Keep history state in sync with current selections
  useEffect(() => {
    if (product) {
      window.history.replaceState(
        { view: 'product', productId: product.id, selections: { color: selectedColor, size: selectedSize, quantity } },
        ''
      );
    }
  }, [selectedColor, selectedSize, quantity, product]);

  if (!product) return null;

  let displayImages = product.images || [];
  if (product.colorImages && selectedColor && product.colorImages[selectedColor]) {
    displayImages = [...product.colorImages[selectedColor]];
  }
  if (product.sizeChart && !displayImages.includes(product.sizeChart)) {
    displayImages = [...displayImages, product.sizeChart];
  }
  const images = displayImages;
  const totalImages = images.length;
  const hasDiscount = product.compareAtPrice && product.compareAtPrice > product.price;
  const discountPercent = hasDiscount
    ? Math.round(((product.compareAtPrice - product.price) / product.compareAtPrice) * 100)
    : 0;

  return (
    <Div>
      <div className="mpv-scroll">
        {/* Back button */}
        <div className="mpv-back-row">
          <button className="mpv-back" onClick={onBack}>← Back to products</button>
        </div>

        {/* Image viewer */}
        <div className="mpv-image-wrap">
        <div
          className="mpv-image"
          onTouchStart={e => {
            swipeRef.current.startX = e.touches[0].clientX;
            swipeRef.current.startY = e.touches[0].clientY;
          }}
          onTouchEnd={e => {
            const dx = e.changedTouches[0].clientX - swipeRef.current.startX;
            const dy = e.changedTouches[0].clientY - swipeRef.current.startY;
            if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
              if (dx < 0) setCurrentImage(i => i < totalImages - 1 ? i + 1 : 0);
              else setCurrentImage(i => i > 0 ? i - 1 : totalImages - 1);
            } else if (Math.abs(dx) < 10 && Math.abs(dy) < 10) {
              setShowZoom(true);
            }
          }}
        >
          {images[currentImage] && <img src={images[currentImage]} alt={product.name} />}
          {hasDiscount && <div className="mpv-badge">-{discountPercent}%</div>}
        </div>
        </div>

        {showZoom && (
          <MobileZoom
            images={images}
            currentImage={currentImage}
            setCurrentImage={setCurrentImage}
            onClose={() => setShowZoom(false)}
          />
        )}

        {/* Image progress + nav */}
        <div className="mpv-nav">
          <div className="mpv-dots">
            {images.map((_, i) => (
              <div
                key={i}
                className={`mpv-dot ${i <= currentImage ? 'mpv-dot--active' : ''}`}
                onClick={() => setCurrentImage(i)}
              />
            ))}
          </div>
          <div className="mpv-arrows">
            <button onClick={() => setCurrentImage(i => i > 0 ? i - 1 : totalImages - 1)}>◀</button>
            <span>{currentImage + 1} / {totalImages}</span>
            <button onClick={() => setCurrentImage(i => i < totalImages - 1 ? i + 1 : 0)}>▶</button>
          </div>
        </div>

        {/* Product info */}
        <div className="mpv-info">
          <h2 className="mpv-name">{product.name}</h2>
          <div className="mpv-price-row">
            <span className="mpv-price">₹{product.price.toLocaleString()}</span>
            {hasDiscount && (
              <>
                <span className="mpv-original">₹{product.compareAtPrice.toLocaleString()}</span>
                <span className="mpv-off">{discountPercent}% OFF</span>
              </>
            )}
          </div>
          <div className="mpv-categories">
            {(product.categories || [product.category]).map(cat => {
              const label = cat === 'music' ? 'BANDS' : cat === 'games' ? 'GAMES' : cat === 'rep' ? 'REP' : 'GOOFY';
              const brandName = cat === 'music' && product.band ? product.band : cat === 'games' && product.game ? product.game : cat === 'rep' && product.brand ? product.brand : null;
              return (
                <button
                  key={cat}
                  className="mpv-category"
                  onClick={() => onOpenCategory && onOpenCategory(cat, brandName)}
                >
                  {label}{brandName ? ` · ${brandName}` : ''}
                </button>
              );
            })}
          </div>

          {/* Colors */}
          {product.colors && product.colors.length > 1 && (
            <div className="mpv-section">
              <label>Colour: {selectedColor}</label>
              <div className="mpv-swatches">
                {product.colors.map(c => (
                  <button
                    key={c}
                    className={`mpv-swatch ${selectedColor === c ? 'mpv-swatch--active' : ''}`}
                    style={{ backgroundColor: getColorHex(c) }}
                    onClick={() => { setSelectedColor(c); setCurrentImage(0); }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Size & Qty */}
          <div className="mpv-size-qty-row">
            <div className="mpv-section mpv-section--size">
              <label>Size: {wasAutoSelected && <span className="mpv-auto-tag">auto-selected</span>}</label>
              <select
                value={selectedSize}
                onChange={e => { setSelectedSize(e.target.value); setWasAutoSelected(false); }}
                className={`mpv-size-select ${!selectedSize ? 'mpv-size-select--empty' : ''}`}
              >
                <option value="">Select size</option>
                {(product.sizes || []).map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="mpv-section mpv-section--qty">
              <label>Qty:</label>
              <div className="mpv-qty">
                <button onClick={() => setQuantity(q => Math.max(1, q - 1))}>−</button>
                <span>{quantity}</span>
                <button onClick={() => setQuantity(q => Math.min(10, q + 1))}>+</button>
              </div>
            </div>
          </div>
        </div>

        {/* Fabric & Care */}
        {product.description && <MobileFabricCare description={product.description} />}

        {/* Actions inside scroll */}
        <div className="mpv-actions">
        <button className="mpv-btn mpv-btn--cart" onClick={() => {
          if (!selectedSize) { onShowDialog && onShowDialog('error', 'Please select a size.'); return; }
          addItem(product, selectedSize, quantity, selectedColor);
          if (typeof window !== 'undefined' && window.gtag) {
            window.gtag('event', 'conversion', {
              send_to: 'AW-18118827880/OilZCNyh0aIcEOi-3b9D',
              value: product.price * quantity,
              currency: 'INR',
            });
          }
          onShowDialog && onShowDialog('success', `Added to cart!`);
        }}>
          Add to Cart
        </button>
        <button className="mpv-btn mpv-btn--buy" onClick={() => {
          if (!selectedSize) { onShowDialog && onShowDialog('error', 'Please select a size.'); return; }
          addItem(product, selectedSize, quantity, selectedColor);
          onOpenCart && onOpenCart({ color: selectedColor, size: selectedSize, quantity });
        }}>
          Buy Now
        </button>
        <button className="mpv-btn mpv-btn--share" onClick={async () => {
          const result = await shareProduct(product);
          if (result === 'copied') onShowDialog && onShowDialog('success', 'Link copied!');
        }}>
          Share
        </button>
      </div>

        {/* Suggested products */}
        {suggested.length > 0 && (
          <div className="mpv-suggested">
            <div className="mpv-suggested__label">You might also like</div>
            <div className="mpv-suggested__grid">
              {suggested.map(p => (
                <div
                  key={p.id}
                  className="mpv-suggested__card"
                  onClick={() => onViewProduct && onViewProduct(p)}
                >
                  <div className={`mpv-suggested__img ${p.isDualPrint ? 'mpv-suggested__img--dual' : ''}`}>
                    <img className="mpv-suggested__img-front" src={p.images[0]} alt={p.name} />
                    {p.isDualPrint && (() => {
                      const dc = p.defaultColor;
                      const backImg = dc && ((p.colorBackImages && p.colorBackImages[dc]) || (p.colorImages && p.colorImages[dc]?.[1]));
                      return backImg ? <img className="mpv-suggested__img-back" src={backImg} alt={p.name + ' back'} /> : null;
                    })()}
                  </div>
                  <div className="mpv-suggested__name">{p.name}</div>
                  <div className="mpv-suggested__price">₹{p.price.toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Div>
  );
}

const Div = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: #1a1a1a;
  color: #c0c0c0;
  font-family: Tahoma, sans-serif;

  /* Mobile Zoom */
  .mzoom {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0, 0, 0, 0.92);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    touch-action: none;
  }
  .mzoom__close {
    position: absolute;
    top: 12px;
    right: 12px;
    background: rgba(0, 0, 0, 0.5);
    border: 1px solid #555;
    color: #ccc;
    font-size: 18px;
    padding: 8px 12px;
    cursor: pointer;
    z-index: 3;
  }
  .mzoom__img {
    max-width: 90%;
    max-height: 80%;
    transition: transform 0.05s linear;
    img {
      max-width: 100%;
      max-height: 80vh;
      object-fit: contain;
      display: block;
    }
  }
  .mzoom__nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(0, 0, 0, 0.5);
    border: 1px solid #555;
    color: #ccc;
    font-size: 16px;
    padding: 14px 12px;
    cursor: pointer;
    z-index: 3;
    &:hover { color: #f5a623; }
  }
  .mzoom__nav--left { left: 8px; }
  .mzoom__nav--right { right: 8px; }
  .mzoom__counter {
    position: absolute;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    color: #888;
    font-size: 12px;
    background: rgba(0, 0, 0, 0.5);
    padding: 4px 12px;
  }

  .mpv-scroll {
    flex: 1;
    overflow-y: auto;
  }

  .mpv-back-row {
    padding: 8px 16px;
  }
  .mpv-back {
    background: none;
    border: none;
    color: #f5a623;
    font-size: 13px;
    font-family: Tahoma, sans-serif;
    cursor: pointer;
    padding: 0;
  }

  .mpv-image-wrap {
    padding: 0 24px;
  }
  .mpv-image {
    width: 100%;
    aspect-ratio: 3/4;
    max-height: 50vh;
    background: #111;
    position: relative;
    border: 1px solid #333;
    img { width: 100%; height: 100%; object-fit: contain; }
  }
  .mpv-badge {
    position: absolute;
    top: 8px;
    left: 8px;
    background: #e53935;
    color: white;
    font-size: 12px;
    font-weight: bold;
    padding: 4px 10px;
  }

  .mpv-nav {
    padding: 10px 16px;
    background: #1a1a1a;
  }
  .mpv-dots {
    display: flex;
    gap: 4px;
    justify-content: center;
    margin-bottom: 8px;
  }
  .mpv-dot {
    flex: 1;
    max-width: 40px;
    height: 3px;
    background: #333;
    cursor: pointer;
    &--active { background: #f5a623; }
  }
  .mpv-arrows {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    button {
      background: #222;
      border: 1px solid #444;
      color: #ccc;
      padding: 6px 14px;
      font-size: 14px;
      cursor: pointer;
    }
    span { color: #777; font-size: 12px; }
  }

  .mpv-info {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .mpv-name {
    font-size: 18px;
    font-weight: bold;
    color: #e0e0e0;
    margin: 0;
  }
  .mpv-price-row {
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-wrap: wrap;
  }
  .mpv-price { font-size: 22px; font-weight: bold; color: #f5a623; }
  .mpv-original { font-size: 14px; color: #666; text-decoration: line-through; }
  .mpv-off { font-size: 11px; color: #4caf50; font-weight: bold; background: rgba(76,175,80,0.1); padding: 2px 6px; }
  .mpv-categories {
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
  }
  .mpv-category {
    font-size: 10px;
    color: #888;
    letter-spacing: 2px;
    padding: 3px 8px;
    background: #2a2a2a;
    border: 1px solid #333;
    width: fit-content;
    cursor: pointer;
    font-family: Tahoma, sans-serif;
    &:active { color: #f5a623; border-color: #f5a623; }
  }

  .mpv-section {
    display: flex;
    flex-direction: column;
    gap: 6px;
    label { color: #999; font-size: 12px; }
  }
  .mpv-swatches {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  .mpv-swatch {
    width: 32px;
    height: 32px;
    border: 2px solid #444;
    cursor: pointer;
    padding: 0;
    &--active { border-color: #f5a623; box-shadow: 0 0 0 1px #f5a623; }
  }
  .mpv-auto-tag {
    font-size: 9px;
    color: #4caf50;
    margin-left: 4px;
    font-style: italic;
  }
  .mpv-size-qty-row {
    display: flex;
    gap: 12px;
    align-items: flex-end;
    padding: 0 12px;
    margin-bottom: 8px;
  }
  .mpv-section--size { flex: 1; }
  .mpv-size-select {
    width: 100%;
    background: #222;
    border: 1px solid #444;
    color: #ccc;
    padding: 8px 10px;
    font-size: 13px;
    font-family: Tahoma, sans-serif;
    &:focus { border-color: #f5a623; outline: none; }
    option { background: #222; color: #ccc; }
    &--empty {
      border-color: #f5a623;
      color: #f5a623;
      animation: mpv-size-pulse 1.6s ease-in-out infinite;
    }
  }
  @keyframes mpv-size-pulse {
    0%, 100% { box-shadow: 0 0 0 rgba(245, 166, 35, 0); }
    50% { box-shadow: 0 0 10px rgba(245, 166, 35, 0.5); }
  }
  .mpv-qty {
    display: flex;
    align-items: center;
    gap: 8px;
    button {
      width: 36px;
      height: 36px;
      background: #222;
      border: 1px solid #444;
      color: #ccc;
      font-size: 18px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    span { font-size: 16px; font-weight: bold; color: #e0e0e0; width: 30px; text-align: center; }
  }

  /* Suggestions */
  .mpv-suggested {
    padding: 16px;
    border-top: 1px solid #2a2a2a;
    margin-top: 12px;
  }
  .mpv-suggested__label {
    font-size: 13px;
    font-weight: bold;
    color: #999;
    margin-bottom: 10px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  .mpv-suggested__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
  .mpv-suggested__card {
    cursor: pointer;
    overflow: hidden;
    min-width: 0;
    &:active { opacity: 0.8; }
  }
  .mpv-suggested__img {
    height: 200px;
    background: #111;
    border: 1px solid #333;
    overflow: hidden;
    position: relative;
    .mpv-suggested__img-front { height: 200px; width: 100%; object-fit: contain; }
    .mpv-suggested__img-back {
      position: absolute; top: 0; left: 0; height: 200px; width: 100%; object-fit: contain;
      opacity: 0;
    }
    &--dual .mpv-suggested__img-back {
      animation: mpv-dual-flip 6s ease-in-out infinite;
    }
    &--dual::after {
      content: '⇆ DUAL';
      position: absolute;
      top: 4px; right: 4px;
      background: rgba(0,0,0,0.6);
      color: #f5a623;
      font-size: 8px;
      padding: 2px 5px;
      pointer-events: none;
      z-index: 1;
    }
  }
  @keyframes mpv-dual-flip {
    0%, 45% { opacity: 0; }
    50%, 95% { opacity: 1; }
    100% { opacity: 0; }
  }
  .mpv-suggested__name {
    font-size: 11px;
    color: #ddd;
    margin-top: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .mpv-suggested__price {
    font-size: 11px;
    color: #f5a623;
    font-weight: bold;
  }

  .mpv-fabric-care {
    border: 1px solid #333;
    background: #1a1a1a;
    padding: 10px 12px;
    margin: 8px 12px;
  }
  .mpv-fabric-care__label {
    font-size: 10px;
    color: #f5a623;
    letter-spacing: 1.5px;
    font-weight: bold;
    text-transform: uppercase;
    cursor: pointer;
    &:active { color: #ffcc66; }
  }
  .mpv-fabric-care__arrow {
    font-size: 8px;
    margin-right: 4px;
  }
  .mpv-fabric-care__text {
    font-size: 11px;
    color: #999;
    line-height: 1.6;
    margin-top: 6px;
    div { margin-bottom: 4px; }
  }
  .mpv-actions {
    display: flex;
    gap: 8px;
    padding: 16px;
    margin-top: 8px;
  }
  .mpv-btn {
    flex: 1;
    padding: 14px;
    font-size: 14px;
    font-weight: bold;
    font-family: Tahoma, sans-serif;
    cursor: pointer;
    border: none;
  }
  .mpv-btn--cart {
    background: linear-gradient(180deg, #f5a623 0%, #d4891a 100%);
    color: #000;
  }
  .mpv-btn--buy {
    background: #333;
    border: 1px solid #555;
    color: #ccc;
  }
  .mpv-btn--share {
    background: linear-gradient(180deg, #3571c4 0%, #2056a0 100%);
    border: 1px solid #173d6e;
    border-top-color: #5a9ae0;
    border-left-color: #5a9ae0;
    color: #fff;
    flex: none;
    padding: 12px 20px;
    &:active {
      background: linear-gradient(180deg, #1a4a8a 0%, #2056a0 100%);
    }
  }
`;

export default MobileProductView;
